import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Stack, IconButton, Collapse, Button, LinearProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { getDoctors, getDoctorSchedule, getPatients, getAppointments } from '../services/api';

const DISMISS_KEY = 'onboarding_checklist_dismissed';

// Pasos verificados con datos que ya existen (sin endpoints nuevos) — cada
// uno detecta si el admin ya avanzó ese punto del setup inicial. El registro
// self-service y el empty state de "creá tu primer médico" (Dashboard.js) ya
// resuelven el primer tramo; esto cubre lo que viene después, donde antes
// el admin quedaba solo para descubrir por su cuenta qué hacer.
const STEPS = [
  { key: 'doctor', label: 'Crear tu primer médico', to: '/doctors' },
  { key: 'schedule', label: 'Cargar el horario de atención', to: '/doctors' },
  { key: 'patient', label: 'Cargar tu primer paciente', to: '/patients' },
  { key: 'appointment', label: 'Agendar tu primer turno', to: '/agenda' },
];

const OnboardingChecklist = () => {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');
  const [status, setStatus] = useState(null); // { doctor, schedule, patient, appointment } booleans, or null while loading

  useEffect(() => {
    if (dismissed) return;
    let active = true;

    (async () => {
      try {
        const doctorsRes = await getDoctors({ limit: 1 });
        const doctors = doctorsRes.data?.items || [];
        const hasDoctor = doctors.length > 0;

        let hasSchedule = false;
        if (hasDoctor) {
          try {
            const scheduleRes = await getDoctorSchedule(doctors[0].id);
            hasSchedule = (scheduleRes.data || []).length > 0;
          } catch { /* sin horario todavía */ }
        }

        const [patientsRes, appointmentsRes] = await Promise.all([
          getPatients({ limit: 1 }),
          getAppointments({ limit: 1 }),
        ]);
        const hasPatient = (patientsRes.data?.items || patientsRes.data || []).length > 0;
        const hasAppointment = (appointmentsRes.data?.items || appointmentsRes.data || []).length > 0;

        if (active) {
          setStatus({ doctor: hasDoctor, schedule: hasSchedule, patient: hasPatient, appointment: hasAppointment });
        }
      } catch {
        if (active) setStatus(null);
      }
    })();

    return () => { active = false; };
  }, [dismissed]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  if (dismissed || !status) return null;

  const doneCount = STEPS.filter(s => status[s.key]).length;
  const allDone = doneCount === STEPS.length;

  // Ya completó todo — no hace falta mostrar nada más, ni ocupar espacio
  // esperando que el usuario lo cierre a mano.
  if (allDone) return null;

  return (
    <Collapse in>
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderColor: 'primary.light', bgcolor: 'primary.50' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Primeros pasos</Typography>
            <Typography variant="body2" color="text.secondary">
              {doneCount} de {STEPS.length} completados — te falta poco para tener todo listo.
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleDismiss} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <LinearProgress
          variant="determinate"
          value={(doneCount / STEPS.length) * 100}
          sx={{ mb: 2, height: 6, borderRadius: 3 }}
        />

        <Stack spacing={1}>
          {STEPS.map(step => {
            const done = !!status[step.key];
            return (
              <Box key={step.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {done
                  ? <CheckCircleIcon fontSize="small" color="success" />
                  : <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
                <Typography
                  variant="body2"
                  sx={{ flex: 1, textDecoration: done ? 'line-through' : 'none', color: done ? 'text.secondary' : 'text.primary' }}
                >
                  {step.label}
                </Typography>
                {!done && (
                  <Button size="small" component={RouterLink} to={step.to} sx={{ textTransform: 'none' }}>
                    Ir →
                  </Button>
                )}
              </Box>
            );
          })}
        </Stack>
      </Paper>
    </Collapse>
  );
};

export default OnboardingChecklist;
